import type { AxiosProgressEvent } from 'axios';
import axios from 'axios';

import { EXCEL_CONTENT_TYPE_HEADER } from '@apple/utils/files/models/excel';
import { encodeSlashes } from '@apple/utils/url';

import type { ProductAudit } from '@apple/features/product/models/management';
import { productAuditSchema } from '@apple/features/product/models/management';
import type { ProductKey } from '@apple/features/product/models/product';

import type { AppleRetailProduct, ProductExcelResult } from '../models/management';
import { appleRetailProductSchema, productExcelResultSchema } from '../models/management';

/** Service wrapper for:
 * - AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 * - Manzanita.Web.Features.Catalog.ProductsController
 * - Manzanita.Web.Features.Catalog.ProductImagesController
 */

/**
 * Gets the product with the given ID.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function getProduct(itemId: ProductKey): Promise<AppleRetailProduct> {
	const response = await axios.get(`/api/management/products/${encodeSlashes(itemId)}`);
	return appleRetailProductSchema.parse(response.data);
}

/**
 * Saves a new or existing product with the given ID and information.
 * @param product - The product to save.
 * @param auditComment - Optional. Only required if the audit filter is enabled on the server.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function saveProduct(
	product: AppleRetailProduct,
	auditComment?: string,
): Promise<void> {
	await axios.put(`/api/management/products/${encodeSlashes(product.itemId)}`, product, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Saves a collection of new, existing, or deleted products.
 * @param products - The products to save.
 * @param auditComment - Optional. Only required if the audit filter is enabled on the server.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function saveProducts(
	products: AppleRetailProduct[],
	auditComment?: string,
): Promise<void> {
	if (products.length === 0) {
		return;
	}

	await axios.post('/api/management/products', products, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Archives the product with the given ID.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function archiveProduct(itemId: ProductKey, auditComment?: string): Promise<void> {
	await axios.delete(`/api/management/products/${encodeSlashes(itemId)}`, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Archives the products with the given IDs.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function archiveProducts(itemIds: ProductKey[], auditComment?: string): Promise<void> {
	if (itemIds.length === 0) {
		return;
	}

	await axios.post('/api/management/products/delete-multiple', itemIds, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Restores the products with the given ID.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function restoreProduct(itemId: ProductKey): Promise<void> {
	await axios.post(`/api/management/products/${encodeSlashes(itemId)}/restore`);
}

/**
 * This method is implemented in the client projects, but the URL is the same in both projects.
 * @see AppleRetail.Web.Features.Catalog.AppleRetailProductsController
 */
export async function getProductAudits(itemId: ProductKey): Promise<ProductAudit[]> {
	const response = await axios.get(`/api/management/products/${encodeSlashes(itemId)}/audits`);
	return productAuditSchema.array().parse(response.data);
}

/**
 * Uploads a product bulk management Excel file to be converted into products.
 * @see AppleRetail.Web.Features.Catalog.ProductsExcelController
 */
export async function uploadProducts(
	file: File,
	onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<ProductExcelResult> {
	const response = await axios.post<unknown>('/api/management/products/bulk/excel', file, {
		headers: {
			...EXCEL_CONTENT_TYPE_HEADER,
		},
		onUploadProgress,
	});

	return productExcelResultSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Catalog.ProductsExcelController */
export function getProductManagementTemplateUrl(): string {
	return '/api/management/products/bulk/excel/generate';
}
